import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { Link, useLocation } from 'react-router-dom';
import { useApiToken } from '../auth/AuthProvider';
import { checkAdmin } from '../services/api';
import HelpMenu from './HelpMenu';
import GuidedTour, { hasTourBeenCompleted } from './GuidedTour';

const DEV_AUTH_BYPASS = process.env.REACT_APP_DEV_AUTH_BYPASS === 'true';

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', fontFamily: 'system-ui, sans-serif' },
  header: { background: '#1a365d', color: 'white', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 24 },
  title: { fontSize: 20, fontWeight: 700, margin: 0 },
  nav: { display: 'flex', gap: 16 },
  navLink: { color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: 14 },
  activeLink: { color: 'white', textDecoration: 'none', fontSize: 14, fontWeight: 600 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 16, fontSize: 14 },
  signOut: { background: 'none', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 13 },
  main: { flex: 1, padding: '24px 32px', maxWidth: 1200, width: '100%', margin: '0 auto' },
};

function MsalLayout({ children }: { children: React.ReactNode }) {
  const { instance, accounts } = useMsal();
  const location = useLocation();
  const userName = accounts[0]?.name || 'User';

  return <LayoutShell userName={userName} onSignOut={() => instance.logoutRedirect()} location={location}>{children}</LayoutShell>;
}

function DevLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return <LayoutShell userName="Dev Test User" onSignOut={() => {}} location={location}>{children}</LayoutShell>;
}

export default function Layout({ children }: { children: React.ReactNode }) {
  if (DEV_AUTH_BYPASS) return <DevLayout>{children}</DevLayout>;
  return <MsalLayout>{children}</MsalLayout>;
}

function LayoutShell({ children, userName, onSignOut, location }: { children: React.ReactNode; userName: string; onSignOut: () => void; location: ReturnType<typeof useLocation> }) {
  const getToken = useApiToken();
  const [isAdmin, setIsAdmin] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    (async () => {
      try {
        const token = await getToken();
        const result = await checkAdmin(token);
        setIsAdmin(result.is_admin);
      } catch {
        // Non-critical
      }
    })();
  }, [getToken]);

  // Show tour on first visit
  useEffect(() => {
    if (!hasTourBeenCompleted()) {
      // Small delay to let the page render first
      const timer = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleStartTour = useCallback(() => setShowTour(true), []);
  const handleCompleteTour = useCallback(() => setShowTour(false), []);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/new-report', label: 'New Report' },
    { path: '/history', label: 'History' },
    ...(isAdmin ? [{ path: '/accounting', label: 'Accounting' }] : []),
  ];

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>CGS Expense Reports</h1>
          <nav style={styles.nav}>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                style={location.pathname === item.path ? styles.activeLink : styles.navLink}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
        <div style={styles.headerRight}>
          <span>{userName}</span>
          <HelpMenu onStartTour={handleStartTour} isAdmin={isAdmin} />
          <button style={styles.signOut} onClick={onSignOut}>
            Sign Out
          </button>
        </div>
      </header>
      <main style={styles.main}>{children}</main>
      {showTour && <GuidedTour isAdmin={isAdmin} onComplete={handleCompleteTour} />}
    </div>
  );
}
